import { Alert, Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { RedemptionRun } from '../../data/types';
import { unitsAt } from '../../domain/holdings';
import { date, DEMO_TODAY, money, unitPrice, units as formatUnits } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, SectionCard, StatusChip } from '../../ui/primitives';
import BankFilePreview from '../common/BankFilePreview';

function RunCard({ run }: { run: RedemptionRun }) {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const fund = scope.fundById.get(run.fundId)!;
  const unitClass = scope.classById.get(run.classId)!;
  const holderIds = new Set(
    state.ledger.filter((entry) => entry.classId === run.classId).map((entry) => entry.holderId),
  );
  const classUnits = Array.from(holderIds).reduce(
    (total, holderId) => total + unitsAt(state.ledger, holderId, run.classId, DEMO_TODAY),
    0,
  );
  const requested = sumBy(run.lines, (line) => line.unitsRequested);
  const gateUnits = classUnits * (run.gatePct / 100);
  const price = run.unitPrice ?? unitClass.unitPrice;
  const priced = run.status !== 'OPEN';
  const gated = priced && run.lines.some((line) => line.unitsRedeemed < line.unitsRequested);

  return (
    <SectionCard
      title={`${fund.code} redemption window · ${date(run.windowDate)}`}
      subtitle={`Requests close ${date(run.cutoffDate)} · liquidity gate ${run.gatePct}% of units on issue`}
      action={<StatusChip status={run.status} />}
    >
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2">
            Requested {formatUnits(requested)} units (
            {((requested / Math.max(1, classUnits)) * 100).toFixed(1)}% of the class)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gate {formatUnits(Math.round(gateUnits))} units
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (requested / Math.max(1, gateUnits)) * 100)}
          color={requested > gateUnits ? 'error' : 'primary'}
          sx={{ height: 8, borderRadius: 4, mt: 0.75 }}
        />
      </Box>
      {gated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Requests exceeded the {run.gatePct}% gate, so every request was scaled back pro rata. The
          unfilled balance carries to the next window unless withdrawn.
        </Alert>
      )}
      <DataTable
        rows={run.lines}
        rowKey={(row) => row.requestId}
        dense
        emptyMessage="No requests yet"
        columns={[
          {
            key: 'holder',
            label: 'Holder',
            render: (row) => scope.holderById.get(row.holderId)?.name,
          },
          {
            key: 'requested',
            label: 'Units requested',
            align: 'right',
            render: (row) => formatUnits(row.unitsRequested),
          },
          {
            key: 'redeemed',
            label: priced ? 'Units redeemed' : 'Est. value',
            align: 'right',
            render: (row) =>
              priced ? formatUnits(row.unitsRedeemed) : money(row.unitsRequested * price),
          },
          {
            key: 'amount',
            label: 'Proceeds',
            align: 'right',
            render: (row) => (priced ? money(row.amount, true) : '—'),
          },
        ]}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {run.status === 'OPEN' && (
          <Button
            variant="contained"
            disabled={!run.lines.length}
            onClick={() => {
              dispatch({ type: 'REDEMPTION_RUN_PRICE', id: run.id });
              toast(`Window closed and priced at ${unitPrice(unitClass.unitPrice)}`);
            }}
          >
            Close window and price (simulate {date(run.windowDate)})
          </Button>
        )}
        {run.status === 'PRICED' && (
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: 'REDEMPTION_RUN_SUBMIT', id: run.id });
              toast('Redemption run sent to Approvals');
            }}
          >
            Submit for approval
          </Button>
        )}
        {run.status === 'PENDING_APPROVAL' && (
          <Button component={RouterLink} to="/manager/approvals" variant="contained">
            Open approvals
          </Button>
        )}
        {run.status === 'APPROVED' && (
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: 'REDEMPTION_RUN_PAY', id: run.id });
              toast('Redemptions paid: units cancelled and confirmations sent');
            }}
          >
            Release payments
          </Button>
        )}
        {run.status === 'PAID' && (
          <Typography variant="body2" color="text.secondary">
            Paid at {unitPrice(price)}, approved by {run.approvedBy}.{' '}
            {money(sumBy(run.lines, (line) => line.amount))} returned.
          </Typography>
        )}
      </Stack>
      {(run.status === 'APPROVED' || run.status === 'PRICED') && run.lines.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <BankFilePreview
            fund={fund}
            description="REDEMPTION"
            paymentDate={run.windowDate}
            fileName={`${fund.code}_REDEMPTIONS_${run.windowDate}.aba`}
            payments={run.lines.map((line) => ({ holderId: line.holderId, amount: line.amount }))}
          />
        </Box>
      )}
    </SectionCard>
  );
}

export default function RedemptionsPage() {
  const scope = useTenantScope();
  const runs = [...scope.redemptionRuns].sort((left, right) =>
    right.windowDate.localeCompare(left.windowDate),
  );
  return (
    <>
      <PageHeader
        title="Redemptions"
        subtitle="Quarterly redemption windows for open-ended funds, priced at NAV and protected by a liquidity gate."
      />
      <AboutThisPage>
        <p>
          Investors request redemptions in the app. Once verified in Transfers &amp; changes, each
          request is queued into the next window. At the window the run is priced at that day&apos;s
          unit price; if requests exceed the gate in the trust deed, every request is scaled back
          pro rata. Payment needs approval, and confirmations go to each investor.
        </p>
      </AboutThisPage>
      {runs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4 }}>
          <Typography color="text.secondary">No redemption windows for this tenant.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {runs.map((run) => (
            <RunCard key={run.id} run={run} />
          ))}
        </Stack>
      )}
    </>
  );
}
